import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import api from '../api/axiosConfig';
import WorkerFormModal from '../components/WorkerFormModal';
import InviteModal from '../components/InviteModal';
import InviteLinkModal from '../components/InviteLinkModal';
import UserPermissionModal from '../components/UserPermissionModal';
import { useModal } from '../contexts/ModalContext';
import { toLocalDateString } from '../utils/localDate';

const ROLE_LABEL = {
    OWNER: '소유자',
    SUPER_ADMIN: '최고 관리자',
    INTERMEDIATE_ADMIN: '중간 관리자',
    STAFF: '일반 작업자',
    CLIENT: '건물주',
};

/**
 * 계정을 등록하고, 그 계정이 무엇을 할 수 있는지 정하는 곳.
 *
 * 권한 관리는 설정의 탭으로 따로 있었다. 같은 `/users` 를 한 번 더 불러 같은
 * 사람들을 늘어놓고, 여기서는 등록·삭제만, 저기서는 역할만 고칠 수 있었다.
 * 사람을 하나 더할 때마다 두 화면을 오가야 했다. 명단은 하나면 된다.
 */
const WorkerListPage = () => {
    const location = useLocation();
    const { facilityId } = location.state || {};

    const [workers, setWorkers] = useState([]);
    const [activeWorkerIds, setActiveWorkerIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedWorker, setSelectedWorker] = useState(null); // For Edit
    const [permissionTarget, setPermissionTarget] = useState(null);
    // 계정은 초대로 만든다. 이 화면에서 바로 만들면 비밀번호를 우리가 정해야
    // 하고, 그 값은 우리가 알게 된다.
    const [inviting, setInviting] = useState(false);
    const [invites, setInvites] = useState([]);
    /** 방금 만든 링크. 다시 볼 수 없으므로 그 자리에서 보여준다. */
    const [link, setLink] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const { showAlert, showConfirm } = useModal();

    // Selection for deletion
    const [selectedIds, setSelectedIds] = useState(new Set());
    /** 비밀번호 재설정을 요청한 사람들의 id. */
    const [resetAsked, setResetAsked] = useState(new Set());

    const fetchInvites = async () => {
        try {
            const { data } = await api.get('/invitations');
            setInvites(data || []);
        } catch (err) {
            console.error('Failed to load invitations', err);
        }
    };

    /** 비밀번호를 잊었다고 손을 든 사람들. */
    const fetchResetRequests = async () => {
        try {
            const { data } = await api.get('/password-resets/requests');
            setResetAsked(new Set((data || []).map((r) => r.user.id)));
        } catch (err) {
            console.error('Failed to load reset requests', err);
        }
    };

    const fetchWorkers = async () => {
        setLoading(true);
        try {
            const [usersRes, reportsRes] = await Promise.all([
                api.get('/users'),
                facilityId ? api.get('/reports', {
                    params: {
                        facilityId: facilityId !== 'all' ? facilityId : undefined,
                        date: toLocalDateString(),
                        type: 'INSPECTION'
                    }
                }) : Promise.resolve({ data: [] })
            ]);

            setWorkers(usersRes.data);

            if (facilityId) {
                // 목록이 한 쪽씩 오게 바뀌었다. 오늘 누가 점검했는지만 보므로
                // 첫 쪽이면 충분하다.
                const reportData = reportsRes.data.items || [];
                const activeIds = new Set(reportData.map(r => r.reporterId));
                setActiveWorkerIds(activeIds);
            }
        } catch (error) {
            console.error('Error fetching workers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWorkers();
        fetchInvites();
        fetchResetRequests();
    }, [facilityId]);

    const handleAddClick = () => setInviting(true);

    /**
     * 링크를 다시 만들어 보여준다.
     *
     * 서버가 토큰 원본을 저장하지 않으므로 되찾을 수는 없다. 새로 만들면 앞의
     * 링크는 죽는다 -- 둘 다 살아 있으면 어느 것이 최신인지 아무도 모른다.
     */
    const resendInvite = async (invite) => {
        try {
            const { data } = await api.post(`/invitations/${invite.id}/resend`);
            setLink({
                name: invite.name,
                url: `${window.location.origin}/invite/${data.token}`,
            });
            fetchInvites();
        } catch (err) {
            showAlert('오류', err.response?.data?.message || '만들지 못했습니다.', 'danger');
        }
    };

    /**
     * 비밀번호를 다시 정할 링크를 만들어 보여준다.
     *
     * 관리자가 새 비밀번호를 지어 알려주는 길은 두지 않는다. 그러면 관리자가
     * 그 사람의 비밀번호를 알게 되고, 바꾸라고 말해도 대개 안 바꾼다.
     */
    const resetPassword = async (worker) => {
        try {
            const { data } = await api.post('/password-resets', { userId: worker.id });
            setLink({
                kind: 'reset',
                name: data.name || worker.name,
                url: `${window.location.origin}/reset/${data.token}`,
                validText: `${data.validHours}시간`,
            });
            // 손 든 것이 내려갔다. 목록을 다시 읽어 표시를 지운다.
            fetchResetRequests();
        } catch (err) {
            showAlert('오류', err.response?.data?.message || '만들지 못했습니다.', 'danger');
        }
    };

    const handleEditClick = (worker) => {
        setSelectedWorker(worker);
        setShowModal(true);
    };

    const handleCheckboxChange = (id, checked) => {
        const newSelected = new Set(selectedIds);
        if (checked) {
            newSelected.add(id);
        } else {
            newSelected.delete(id);
        }
        setSelectedIds(newSelected);
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            const allIds = new Set(workers.map(w => w.id));
            setSelectedIds(allIds);
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleDelete = async () => {
        const confirmed = await showConfirm(
            '사용자 삭제',
            `${selectedIds.size}명의 계정을 완전히 삭제할까요?`,
            'danger'
        );
        if (!confirmed) return;

        try {
            await api.post('/users/batch-delete', { ids: Array.from(selectedIds) });
            setSelectedIds(new Set());
            fetchWorkers();
            showAlert('성공', '계정을 삭제했습니다.');
        } catch (error) {
            console.error('Error deleting workers:', error);
            showAlert('실패', '계정 삭제에 실패했습니다.', 'danger');
        }
    };

    const filteredWorkers = workers.filter(w => {
        const matchesSearch = w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            w.email?.toLowerCase().includes(searchQuery.toLowerCase());

        if (facilityId) {
            return matchesSearch && activeWorkerIds.has(w.id);
        }
        return matchesSearch;
    });

    return (
        <div className="worker-page pb-5" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="pb-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                    <div>
                        <h4 className="fw-bold mb-1">사용자</h4>
                        <p className="text-muted small mb-0">계정을 등록하고 역할과 권한을 정합니다.</p>
                    </div>
                    <div className="d-flex gap-2">
                        {/* 봉투는 '메일' 로 읽힌다. 사람을 더하는 자리라는 것이
                            먼저 보여야 하므로 사람 모양에 더하기를 쓴다. 글자도
                            숨기지 않는다 -- 아이콘만 남으면 무엇을 더하는지
                            알 수 없다. */}
                        <Button variant="primary" className="text-white fw-bold px-3 d-flex align-items-center text-nowrap" onClick={handleAddClick}>
                            <i className="bi bi-person-plus-fill me-2"></i>
                            사용자 초대
                        </Button>
                    </div>
                </div>

                {/* 아직 가입하지 않은 사람. 링크를 보냈는데 소식이 없으면 여기서
                    보인다 -- 안 보이면 보냈는지조차 알 수 없다. */}
                {invites.length > 0 && (
                    <div className="bg-white p-3 rounded-3 border mb-3">
                        <div className="fw-bold small mb-2">가입 대기 {invites.length}</div>
                        {invites.map((iv) => (
                            <div
                                key={iv.id}
                                className="d-flex align-items-center gap-2 py-2 border-top"
                                style={{ fontSize: '0.85rem' }}
                            >
                                <span className="fw-medium">{iv.name}</span>
                                <span className="text-muted text-truncate">{iv.email}</span>
                                <span className="text-muted ms-auto text-nowrap" style={{ fontSize: '0.75rem' }}>
                                    {new Date(iv.expiresAt).getMonth() + 1}/
                                    {new Date(iv.expiresAt).getDate()}까지
                                </span>
                                {/* 링크를 잃어버리는 일이 흔하다. 취소하고 다시
                                    만들게 하면 같은 일을 두 동작으로 시킨다. */}
                                <Button
                                    variant="link"
                                    className="p-0 text-decoration-none text-nowrap"
                                    style={{ fontSize: '0.8rem' }}
                                    onClick={() => resendInvite(iv)}
                                >
                                    링크
                                </Button>
                                <Button
                                    variant="link"
                                    className="text-danger p-0 text-decoration-none"
                                    style={{ fontSize: '0.8rem' }}
                                    onClick={async () => {
                                        await api.delete(`/invitations/${iv.id}`);
                                        fetchInvites();
                                    }}
                                >
                                    취소
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Filter & Count */}
                <div className="bg-white p-3 rounded-3 border mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="fw-bold">
                        전체 <span className="text-indigo">{workers.length}</span>
                    </div>
                    <div className="position-relative flex-grow-1 search-min-width" style={{ minWidth: '220px' }}>
                        <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <input
                            type="text"
                            className="form-control ps-5 bg-light border-0"
                            placeholder="이름 또는 이메일 검색"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* List Content */}
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="d-none d-md-block bg-white rounded-3 border overflow-hidden">
                            <table className="table table-hover mb-0">
                                <thead className="bg-light text-muted small">
                                    <tr>
                                        <th className="py-2 ps-4" style={{ width: '50px' }}>
                                            <Form.Check
                                                type="checkbox"
                                                checked={selectedIds.size === workers.length && workers.length > 0}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                        </th>
                                        <th className="py-2">이름</th>
                                        <th className="py-2">이메일</th>
                                        <th className="py-2">권한</th>
                                        <th className="py-2">비고</th>
                                        <th className="py-2 text-end pe-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredWorkers.map(worker => (
                                        <tr key={worker.id} style={{ cursor: 'pointer' }} onClick={() => handleEditClick(worker)}>
                                            <td className="ps-4 align-middle" onClick={(e) => e.stopPropagation()}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedIds.has(worker.id)}
                                                    onChange={(e) => handleCheckboxChange(worker.id, e.target.checked)}
                                                />
                                            </td>
                                            <td className="align-middle fw-medium">
                                                {worker.name} {worker.role === 'OWNER' && '👑'}
                                            </td>
                                            <td className="align-middle text-secondary small">{worker.email}</td>
                                            <td className="align-middle small">{ROLE_LABEL[worker.role] || worker.role}</td>
                                            <td className="align-middle text-secondary small">{worker.memo || '-'}</td>
                                            <td className="align-middle text-end pe-4" onClick={(e) => e.stopPropagation()}>
                                                {/* 손을 든 사람은 단추가 눈에
                                                    띄어야 한다. 목록을 훑다가
                                                    지나치면 요청이 묻힌다. */}
                                                <Button
                                                    variant="link"
                                                    className={`p-0 text-decoration-none small me-3 ${
                                                        resetAsked.has(worker.id) ? 'text-danger fw-bold' : 'text-secondary'
                                                    }`}
                                                    onClick={() => resetPassword(worker)}
                                                >
                                                    {resetAsked.has(worker.id) ? '비밀번호 요청함' : '비밀번호'}
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    className="text-secondary p-0 text-decoration-none small"
                                                    onClick={() => setPermissionTarget(worker)}
                                                >
                                                    권한
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredWorkers.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5 text-muted">검색 결과가 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="d-md-none">
                            {filteredWorkers.map(worker => (
                                <div key={worker.id} className="bg-white p-3 rounded-3 border mb-3" onClick={() => handleEditClick(worker)}>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedIds.has(worker.id)}
                                                    onChange={(e) => handleCheckboxChange(worker.id, e.target.checked)}
                                                />
                                            </div>
                                            <div className="fw-bold">{worker.name}</div>
                                        </div>
                                        <span className="badge bg-light text-dark border fw-normal">
                                            {ROLE_LABEL[worker.role] || worker.role}
                                        </span>
                                    </div>
                                    <div className="ps-4 ms-1">
                                        <div className="mb-1 text-muted text-truncate"><i className="bi bi-envelope me-2"></i>{worker.email}</div>
                                        {worker.memo && <div className="mt-2 text-secondary small bg-light p-2 rounded">{worker.memo}</div>}
                                        <div className="mt-2 d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="light"
                                                size="sm"
                                                className="border rounded-pill px-3"
                                                onClick={() => setPermissionTarget(worker)}
                                            >
                                                권한 설정
                                            </Button>
                                            <Button
                                                variant={resetAsked.has(worker.id) ? 'danger' : 'light'}
                                                size="sm"
                                                className={resetAsked.has(worker.id) ? 'rounded-pill px-3 text-white' : 'border rounded-pill px-3'}
                                                onClick={() => resetPassword(worker)}
                                            >
                                                {resetAsked.has(worker.id) ? '비밀번호 요청함' : '비밀번호 재설정'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {filteredWorkers.length === 0 && (
                                <div className="text-center py-5 text-muted">검색 결과가 없습니다.</div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Delete Action Bar */}
            {selectedIds.size > 0 && (
                <div className="position-fixed bottom-0 start-0 w-100 bg-white border-top p-3 d-flex justify-content-between align-items-center shadow-lg" style={{ zIndex: 1050, paddingLeft: '280px' }}> {/* Adjust paddingLeft if sidebar is persistent, or use media query */}
                    {/* Note: In Layout.js, main-wrapper handles sidebar shift. This fixed bar might overlap sidebar or be full width. 
                        Ideally it should be inside main-wrapper or handled globally. 
                        For now, full width is fine but on desktop with sidebar it might look slightly off if it covers sidebar. 
                        But sidebar has z-index probably. Check Layout.css? 
                        I'll just center the content or make it full width. */}
                    <div className="container-fluid d-flex justify-content-between align-items-center">
                        <span className="fw-bold">{selectedIds.size}명 선택됨</span>
                        <Button variant="danger text-white" onClick={handleDelete}>
                            삭제하기
                        </Button>
                    </div>
                </div>
            )}

            <InviteLinkModal link={link} onClose={() => setLink(null)} />

            <InviteModal
                show={inviting}
                onClose={() => setInviting(false)}
                onSuccess={(made) => {
                    setLink(made);
                    fetchInvites();
                }}
            />

            <WorkerFormModal
                show={showModal}
                worker={selectedWorker}
                onClose={() => setShowModal(false)}
                onSuccess={() => {
                    fetchWorkers();
                }}
            />

            <UserPermissionModal
                show={Boolean(permissionTarget)}
                user={permissionTarget}
                onClose={() => setPermissionTarget(null)}
                onSuccess={fetchWorkers}
            />
        </div>
    );
};

export default WorkerListPage;
